<template>
  <div
    id="bookingRecap"
    ref="bookingRecap"
    class="BookingRecap"
    :class="{
      'BookingRecap--expanded': expanded,
    }">
    <div class="BookingRecap-header">
      <ai-typo
        as="div"
        variant="button-regular"
        class="BookingRecap-header-title">
        {{ $t('booking.recap.widget.title') }}
      </ai-typo>

      <ai-button
        class="BookingRecap-header-close"
        variant="tertiary"
        @click="toggleExpand">
        <ai-icon name="close" class="BookingRecap-header-closeIcon" />
      </ai-button>
    </div>

    <booking-recap-hotel class="BookingRecap-hotel" />

    <div class="BookingRecap-booking">
      <booking-recap-summary />

      <client-only>
        <ai-separator v-if="bookingStore.rooms.length > 0" :margin="2" />
      </client-only>

      <div class="BookingRecap-rooms" v-if="allRoomsValid">
        <div
          v-for="(room, index) in bookingStore.rooms"
          :key="room.productCode"
          class="BookingRecap-room">
          <ai-separator v-if="index > 0" :margin="2" />

          <booking-recap-room
            ref="roomElements"
            :booking-room="room"
            :index="index"
            :multiple="bookingStore.rooms.length > 1" />
        </div>
      </div>
    </div>
    <div v-if="showPromoCode && bookingStore.isoDateIn">
      <booking-promo-code
        class="BookingRecap-promoCode"
        @promo-code-applied="handlePromoCodeApply" />
    </div>

    <div
      v-if="typeof totalAmount === 'number' && totalCurrency"
      class="BookingRecap-priceAndDiscount">
      <booking-recap-discount />

      <booking-price-to-pay
        :amount="totalAmount"
        :currency="totalCurrency"
        no-container-style />
    </div>
  </div>

  <!-- Mobile widget -->
  <div
    class="BookingRecap-widget"
    :class="{
      'BookingRecap-widget--expanded': expanded,
      'BookingRecap-widget--bottom': isFooter,
    }">
    <div class="BookingRecap-total">
      <template v-if="convertedTotal.amount">
        <ai-amount
          :amount="convertedTotal.amount"
          :symbol="convertedTotal.currency?.code"
          decimal-typo="paragraph-04-bold"
          main-typo="paragraph-02-bold" />

        <ai-typo as="span" variant="overline-01">
          {{ $t('booking.recap.widget.allAmount') }}
        </ai-typo>
      </template>
    </div>

    <button
      type="button"
      class="AiButton BookingRecapToggle"
      aria-controls="bookingRecap"
      :aria-expanded="expanded"
      @click="toggleExpand">
      <span class="AiLabel">
        {{ $t('booking.recap.widget.summary') }}
      </span>

      <span class="AiButton-icon AiButton-icon--right">
        <span
          class="AiIcon AiIcon--chevron BookingRecap-widget-icon"
          :class="{
            'BookingRecap-widget-icon--expanded': expanded,
            'BookingRecap-widget-icon--bottom': isFooter,
          }">
          <svg class="AiIcon-svg" aria-hidden="true">
            <use :href="iconChevronPath" />
          </svg>
        </span>
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { useLayoutStore } from '~/domains/ux/layout.store';
import { eventCustom } from '~/helpers';

import {
  addTrapFocusToElement,
  focusableElements,
  removeTrapFocusToElement,
} from '../../../ux/helpers/trapFocus';
import { useBookingStore } from '../../store';
import BookingPriceToPay from '../BookingPriceToPay/BookingPriceToPay.vue';

import BookingRecapDiscount from './BookingRecapDiscount.vue';
import BookingRecapHotel from './BookingRecapHotel.vue';
import BookingRecapRoom from './BookingRecapRoom.vue';
import BookingRecapSummary from './BookingRecapSummary.vue';

interface Props {
  allRoomsValid: any;
}
const props = defineProps<Props>();

const bookingStore = useBookingStore();
const { currentStep } = useCurrentBookingStep();
const emit = defineEmits(['promoCodeApplied']);

const { t } = useI18n();

watch(
  () => bookingStore.wantSignup,
  () => {
    nextTick(() => {
      const instance = getCurrentInstance();
      instance?.proxy?.$forceUpdate();
    });
  },
);

// Workaround
// Combination "aibutton + aicon in slot" is crashing for some reason during route upd.
// Need to refactor it
const iconChevronPath = computed<string>(() => {
  return '/assets/' + 'global-sprites.svg' + '#chevron';
});

const layoutStore = useLayoutStore();

const roomElements = ref<(typeof BookingRecapRoom)[]>([]);
const bookingRecap = ref<HTMLElement>();

const points = computed(() => bookingStore.burntPoints);

const selectedVoucher = computed(() => {
  if (
    !bookingStore.currentBasket ||
    !Array.isArray(bookingStore.currentBasket?.vouchers)
  )
    return;

  return bookingStore.currentBasket.vouchers.find(
    voucher => voucher.points === points.value,
  );
});

const voucherAmount = computed(() => selectedVoucher.value?.price?.amount ?? 0);

const totalAmountWithoutVoucher = computed(() => {
  return roomElements.value.reduce((total, room) => {
    const roomPrice =
      (room.offer?.pricing?.amount?.hotelKeeper || 0) +
      (room.offer?.pricing?.amount?.otherTax?.amount?.excluded || 0) +
      (room.offer?.pricing?.amount?.vat?.amount?.excluded || 0) +
      (room.offer?.pricing?.amount?.fees?.amount?.excluded || 0);

    const enhancementsPrice = room.options?.reduce(
      (subtotal: number, enhancement: any) =>
        subtotal +
        ((enhancement.apiOption?.pricing?.amount?.hotelKeeper || 0) +
          (enhancement?.apiOption?.pricing?.amount?.otherTax?.amount
            ?.excluded || 0)) *
          enhancement.bookingOption.quantity,
      0,
    );

    return total + roomPrice + enhancementsPrice;
  }, 0);
});

const totalAmount = computed(() => {
  return totalAmountWithoutVoucher.value - voucherAmount.value;
});

const totalCurrency = computed(
  () => roomElements.value?.[0]?.offer?.pricing?.currency as string,
);
const userCurrency = computed(() => bookingStore.currentCurrency);

const showPromoCode = computed(
  () => currentStep.value !== 'details' && currentStep.value !== 'payment',
);

const convertedTotal = useConvertToUserCurrency(
  totalAmount,
  totalCurrency,
  userCurrency,
);

const expanded = ref(false);

function toggleExpand() {
  eventCustom('Generic Event', {
    event_name: 'CTA_buttonclick',
    click_interaction: {
      event_info: 'tertiary',
      click_url: '',
      click_text: t('booking.recap.widget.summary'),
      click_location: window.location.href,
      click_type: 'button',
    },
  });

  expanded.value = !expanded.value;
}

const isFooter = ref(false);
const bookingFooterObserver = ref<IntersectionObserver>();

const handlePromoCodeApply = () => {
  emit('promoCodeApplied');
};

onMounted(() => {
  addTrapFocusToElement(bookingRecap.value);
  const bottom = document.querySelector('footer');

  if (!bottom) return;

  bookingFooterObserver.value = new IntersectionObserver(entries => {
    for (const entry of entries) {
      isFooter.value = entry.isIntersecting;
    }
  });

  bookingFooterObserver.value.observe(bottom);
});

onBeforeUnmount(() => {
  removeTrapFocusToElement(bookingRecap.value);
  bookingFooterObserver.value?.disconnect?.();
});

watch(
  () => expanded.value,
  drawerOpen => {
    if (drawerOpen) {
      layoutStore.setScrollLocked(true);
      nextTick(() => {
        const firstFocusableElement = bookingRecap.value?.querySelectorAll(
          focusableElements,
        )[0] as HTMLElement;
        firstFocusableElement?.focus();
      });
    } else {
      layoutStore.setScrollLocked(false);
    }
  },
);
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/fonts';
@use '@/assets/styles/utilities/functions' as func;
@use '@/assets/styles/themes/rixos/constants' as RixosTheme;

.BookingRecap {
  z-index: 120;
  background-color: colors.$white;
  padding: constants.$padding-01;
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  transform: translateY(100%);
  transition: all 300ms;
  overflow: scroll;
  padding-bottom: calc(
    func.calcRem(78) + constants.$padding-02
  ); // Height of widget + padding

  &--expanded {
    transform: translateY(0%);
  }

  @media (mq.$from-large) {
    z-index: 100;
    position: sticky;
    top: constants.$margin-01;
    left: unset;
    right: unset;
    bottom: unset;
    transform: translateY(0);
    overflow: unset;
    padding-bottom: constants.$padding-01;
  }
}

.BookingRecap-hotel {
  margin-bottom: constants.$margin-01;
}

.BookingRecap-booking {
  background-color: colors.$gold-100;
  padding: constants.$padding-01;
  margin-bottom: constants.$inner-00;
}

.BookingRecap-priceAndDiscount {
  background-color: colors.$gold-100;
  padding: constants.$padding-01;
}

.BookingRecap-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: constants.$padding-01;
  border-bottom: 1px solid colors.$gold-200;
  margin: 0 calc(-1 * constants.$padding-01);
  margin-bottom: constants.$margin-02;
  text-transform: uppercase;

  @media (mq.$from-large) {
    display: none;
  }
}

.BookingRecap-header-title {
  color: colors.$gold-900;
}

.BookingRecap-header-close {
  position: absolute;
  right: func.calcRem(16);
  top: func.calcRem(8);
}

.BookingRecap-widget {
  position: fixed;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  z-index: 120;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: func.calcRem(78);
  padding: constants.$padding-01;
  background-color: colors.$white;
  box-shadow: 0px -12px 12px rgba(155, 140, 96, 0.1);

  &--bottom {
    top: 0;
    bottom: initial;
  }

  @media (mq.$from-large) {
    display: none;
  }
}

.BookingRecap-total {
  display: flex;
  flex-direction: column;
}

.BookingRecapToggle {
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  text-decoration: none;

  @include mixins.rem-fallback(min-height, 48);
  @include mixins.rem-fallback(padding-left, 24);
  @include mixins.rem-fallback(padding-right, 24);
  @include mixins.rem-fallback(min-height, 0);

  padding-inline: 0;
  background-color: transparent;
  color: colors.$gold-900;
  fill: colors.$gold-900;

  &:active:not(:disabled) {
    color: colors.$gold-600;
  }

  &:hover {
    text-decoration: underline;
  }

  .AiIcon {
    display: inline-block;
    font-size: 0;
    vertical-align: middle;
    &--right {
      margin-left: constants.$inner-02;
    }
  }

  .AiLabel {
    @include fonts.button-bold-underline;
    text-decoration: none;
    text-transform: uppercase;
  }

  .BookingRecap-widget-icon {
    display: inline-block;
    transition: all 200ms;
    margin-left: constants.$inner-02;

    svg {
      width: 2.4rem;
      height: 2.4rem;
    }

    &--expanded {
      transform: rotate(180deg);
    }

    &--bottom {
      transform: rotate(180deg);

      &.BookingRecap-widget-icon--expanded {
        transform: rotate(360deg);
      }
    }
  }
}

.theme.rixos {
  .BookingRecap {
    border: 1px solid RixosTheme.$color-primary-demi-black;

    .BookingRecap-hotel,
    .BookingRecap-booking {
      border-bottom: 1px solid RixosTheme.$color-primary-demi-black;
    }

    .BookingRecap-booking {
      background: colors.$white;
    }

    :deep(.AiIcon) {
      color: RixosTheme.$color-primary-black;
    }
  }
}
</style>
